import { HttpErrorResponse, HttpRequest, HttpResponse } from '@angular/common/http';
import { SKIP_SUCCESS_NOTIFICATION } from '@core/http/skip-success-notification.context';
import { NotificationService } from '@core/services/notification';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ApiResponseService {
  constructor(private notification: NotificationService) {}

  /**
   * Automatically handles success responses if they contain a message
   */
  handleSuccess(event: any, req?: HttpRequest<any>) {
    if (event instanceof HttpResponse) {
      // Skip GET requests and calls that opt out via HttpContext (avoids custom headers / CORS)
      if (
        req?.method === 'GET' ||
        req?.context.get(SKIP_SUCCESS_NOTIFICATION) ||
        req?.headers.has('X-Skip-Success-Notification')
      ) {
        return;
      }

      const body = event.body;
      
      // ✅ Skip notifications for bypass cases (e.g., phone not verified)
      if (body?.code === 'PHONE_NOT_VERIFIED') {
        return;
      }

      if (body?.message) {
        this.notification.showSuccess(body.message);
      } else if (body?.message_ar) {
        this.notification.showSuccess(body.message_ar);
      }
    }
  }

  /**
   * Automatically handles and parses error responses
   */
  handleError(error: HttpErrorResponse): string {
    let message = 'An unexpected error occurred.';

    if (error.status === 0) {
      message = 'Unable to connect to the server. Please check your internet or the backend status.';
    } else if (error.status >= 500) {
      message = `Server Error (${error.status}): The backend might be down.`;
    } else if (error.error) {
      const errorBody = error.error;
      if (typeof errorBody === 'string') {
        message = errorBody;
      } else {
        // 1. Try to get a specific message from details first as it's usually more precise
        const details = errorBody.details;
        if (details) {
          if (details.error_ar && Array.isArray(details.error_ar)) {
            message = details.error_ar.join(', ');
          } else if (details.error && Array.isArray(details.error)) {
            message = details.error.join(', ');
          } else {
            const detailMessages = Array.from(new Set(Object.values(details).flat()));
            if (detailMessages.length > 0) {
              message = detailMessages.join(', ');
            }
          }
        } 
        
        // 2. If no details or details resulted in an empty message, use the top-level error/message fields
        if (!details || !message || message === 'An unexpected error occurred.') {
          message = errorBody.message_ar || errorBody.error_ar || errorBody.message || errorBody.error || errorBody.detail || message;
        }
      }
    } else {
      message = error.message || 'Unknown error occurred';
    }

    // Skip notification for 401 as it's typically handled by refresh logic
    // unless it's a login or refresh request itself.
    const isAuthPath = error.url?.includes('login') || error.url?.includes('refresh');
    if (error.status !== 401 || isAuthPath) {
      this.notification.showError(message);
    }
    
    return message;
  }
}
